import type { EmployeeData } from "./usePaygapData";

/**
 * Employee data mapping utilities.
 * Converts raw CSV/Excel rows to EmployeeData objects.
 */

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;
const DATE_PATTERN = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/;
const SERIAL_DATE_THRESHOLD = 2500;

/**
 * Convert an Excel serial date number to a DD/MM/YYYY string.
 * Returns null if the value is not a plausible serial date.
 */
export const excelSerialToDateString = (serial: number): string | null => {
  if (!Number.isFinite(serial) || serial < SERIAL_DATE_THRESHOLD) return null;

  const date = new Date(EXCEL_EPOCH + serial * MS_PER_DAY);

  if (isNaN(date.getTime())) return null;

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Convert a DD/MM/YYYY (or DD.MM.YYYY) date string to an Excel serial number.
 * Returns null if the string is not a valid date.
 */
export const dateStringToExcelSerial = (str: string): number | null => {
  const match = str.trim().match(DATE_PATTERN);

  if (!match) return null;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  const utc = Date.UTC(year, month - 1, day);

  if (isNaN(utc)) return null;

  return Math.floor((utc - EXCEL_EPOCH) / MS_PER_DAY);
};

/**
 * Format a value for display in age or yearsOfService columns.
 * Converts Excel serial numbers to DD/MM/YYYY; passes other values through.
 */
export const formatDateOrNumericValue = (
  value: string | number | undefined,
): string => {
  if (value == null || value === "") return "-";

  if (typeof value === "number") {
    const dateStr = excelSerialToDateString(value);

    return dateStr ?? String(value);
  }

  return String(value);
};

/**
 * Convert a date/numeric field value to a number for the API.
 * Handles DD/MM/YYYY strings, Excel serial numbers, plain numbers, and years.
 */
export const dateFieldToNumber = (value: string | number): number => {
  if (typeof value === "number") return value;

  const serial = dateStringToExcelSerial(String(value));

  if (serial !== null) return serial;

  return parseInt(String(value)) || 0;
};

/**
 * Gender format type for auto-detection
 */
export type GenderFormat = "M/F" | "0/1" | "1/2";

/**
 * Normalize a string value by removing BOM, non-breaking spaces, and extra whitespace
 */
export const normalize = (s: string): string =>
  s
    .replace(/^\uFEFF/, "") // strip BOM
    .replace(/\u00A0/g, " ") // nbsp -> space
    .replace(/\s+/g, " ") // collapse whitespace
    .trim();

/**
 * Parse a numeric field, returning the original string if not a valid number.
 * Only converts values that are purely numeric (integers or decimals).
 */
export const parseNumericField = (value: string): string | number => {
  const trimmed = value.trim();

  if (trimmed === "" || !/^-?\d+(\.\d+)?$/.test(trimmed)) return value;

  const num = Number(trimmed);

  return isNaN(num) ? value : num;
};

/**
 * Detect gender format from raw gender values
 */
export const detectGenderFormat = (rawGenderValues: string[]): GenderFormat => {
  const values = rawGenderValues
    .map((v) => normalize(v).toLowerCase())
    .filter((v) => v !== "");

  // Check for text format (M/F/Male/Female)
  const hasTextValues = values.some((v) =>
    ["m", "f", "male", "female", "man", "woman", "muz", "zena", "m", "z"].includes(
      v,
    ),
  );

  if (hasTextValues) return "M/F";

  // Analyze numeric values
  const numericValues = new Set(values.filter((v) => /^[0-2]$/.test(v)));
  const hasZero = numericValues.has("0");
  const hasOne = numericValues.has("1");
  const hasTwo = numericValues.has("2");

  // 1/2 format: has 1 and 2, but no 0
  if (hasOne && hasTwo && !hasZero) return "1/2";

  // Default to 0/1 format
  return "0/1";
};

/**
 * Parse a gender field, normalizing various formats to 0 (male), 1 (female), or 2 (other)
 */
export const parseGenderField = (
  value: string,
  format: GenderFormat = "0/1",
): number => {
  const normalizedValue = normalize(value).toLowerCase();

  // Text values (same for all formats)
  if (["m", "male", "man", "m", "muz"].includes(normalizedValue)) return 0;
  if (["f", "female", "woman", "z", "zena"].includes(normalizedValue)) return 1;
  if (["other", "o"].includes(normalizedValue)) return 2;

  // Numeric values based on format
  if (format === "1/2") {
    if (normalizedValue === "1") return 0; // 1 = Male
    if (normalizedValue === "2") return 1; // 2 = Female
  } else {
    // 0/1 format (default)
    if (normalizedValue === "0") return 0;
    if (normalizedValue === "1") return 1;
    if (normalizedValue === "2") return 2;
  }

  return parseInt(value) || 0;
};

/**
 * Field mapping configuration for CSV/Excel headers
 */
const FIELD_MAPPINGS: Record<keyof EmployeeData, string[]> = {
  key: [
    "Key (personnel number, ID, etc.)",
    "Key (personnel number,ID,etc.)",
    "Key",
    "ID",
    "Personnel number",
    "Employee ID",
  ],
  age: [
    "Age (date of birth, year of birth, age)",
    "Age (date of birth,year of birth,age)",
    "Age",
    "Věk",
    "Vek",
    "Date of birth",
    "Year of birth",
  ],
  gender: ["Gender", "Sex", "Pohlaví", "Pohlavie"],
  yearsOfService: [
    "Years of service (date of entry, year of entry, number of years of service)",
    "Years of service (date of entry,year of entry,number of years of service)",
    "Years of service",
    "Service years",
    "Délka praxe u zaměstnavatele",
    "Počet let služby",
    "Roky služby",
  ],
  educationAndTraining: [
    "Education and training",
    "Education",
    "Training",
    "Nejvyšší dosažené vzdělání",
    "Vzdělání a trénink",
    "Vzdelávanie a odborná príprava",
  ],
  employeeScarcity: [
    "Employee Scarcity",
    "Employee scarcity",
    "Scarcity",
    "Nedostatok zamestnancov",
    "Nedostatek zaměstnanců",
  ],
  employeeEvaluation: [
    "Employee Evaluation",
    "Employee evaluation",
    "Evaluation",
    "Hodnotenie zamestnanca",
    "Hodnocení zaměstnance",
  ],
  category: [
    "Category",
    "Employee category",
    "Employee Category",
    "Kategorie",
    "Kategória",
  ],
  function: [
    "Function (job carried out)",
    "Function",
    "Job",
    "Pracovní místo",
    "Job Title",
    "Název pozice",
    "Názov pracovnej pozície",
  ],
  levelOfRequirements: [
    "Level of requirements (relevant for module 1)",
    "Level of requirements",
    "Level of Requirements - 1",
    "Requirements level",
    "Úroveň dovedností",
    "Úroveň požadavků - 1",
    "Úroveň požiadaviek - 1",
  ],
  levelOfRequirements2: [
    "Level of requirements 2 (relevant for module 1)",
    "Level of requirements 2",
    "Requirements level 2",
    "Level of Requirements - 2",
    "Úroveň požadavků - 2",
    "Úroveň požiadaviek - 2",
  ],
  levelOfRequirements3: [
    "Level of requirements 3 (relevant for module 1)",
    "Level of requirements 3",
    "Requirements level 3",
    "Level of Requirements - 3",
    "Úroveň požadavků - 3",
    "Úroveň požiadaviek - 3",
  ],
  professionalPosition: [
    "Professional position (relevant for module 1)",
    "Professional position",
    "Position",
    "Seniority",
    "Seniorita",
    "Úroveň odpovědnosti",
    "Seniorita",
  ],
  activityRatePercent: [
    "Employees paid monthly: Activity rate in %:",
    "Employees paid monthly: Activity rate in %",
    "Employees paid monthly in %",
    "Activity rate in %",
    "Activity rate",
    "Výše úvazku v %",
    "Employee paid monthly",
    "Zaměstnanec placen měsíčně",
    "Zamestnanec platený mesačne",
  ],
  paidHours: [
    "Employees paid hourly: number of paid hours during the reference month",
    "Employees paid hourly in hours",
    "Paid hours",
    "Hours",
    "Průměrný měsíční počet hodin",
    "Employee paid hourly",
    "Zaměstnanec placen Hodinově",
    "Zamestnanec platený hodinovo",
  ],
  basicWage: [
    "Basic wage (according to activity rate)",
    "Basic wage",
    "Wage",
    "Základní měsíční mzda/platový tarif",
    "Basic Wage",
    "Základní mzda",
    "Základná mzda",
  ],
  wageCompensationForVacation: [
    "Wage Compensation for Vacation",
    "Wage compensation for vacation",
    "Vacation compensation",
    "Náhrada mzdy za dovolenou",
    "Náhrada mzdy za dovolenku",
  ],
  wageCompensationDoctorFamilyLeave: [
    "Wage Compensation for Doctor and Family Leave",
    "Wage compensation for doctor and family leave",
    "Doctor family leave",
    "Náhrada mzdy za ošetřování a rodinnou péči",
    "Náhrada mzdy za ošetrovanie a rodinnú starostlivosť",
  ],
  sickLeaveIncomeCompensation: [
    "Sick Leave Income Compensation",
    "Sick leave income compensation",
    "Sick leave",
    "Náhrada příjmu při dočasné pracovní neschopnosti",
    "Náhrada príjmu pri dočasnej pracovnej neschopnosti",
  ],
  allowances: [
    "Allowances (Shift, Sunday and night work plus other extra pay for difficult working conditions)",
    "Allowances",
    "Příplatky kromě osobního",
    "Příplatky",
    "Příspěvky",
    "Príplatky",
    "Príspevky",
  ],
  overtimeWage: [
    "Overtime Wage",
    "Overtime wage",
    "Mzda za přesčas",
    "Mzda za nadčas",
  ],
  overtimeAllowance: [
    "Overtime Allowance",
    "Overtime allowance",
    "Příplatek za přesčas",
    "Príplatok za nadčas",
  ],
  riskAllowance: [
    "Risk Allowance",
    "Risk allowance",
    "Rizikový příplatek",
    "Rizikový príplatok",
  ],
  standbyDuty: [
    "Standby Duty",
    "Standby duty",
    "Pohotovost",
    "Pohotovosť",
  ],
  thirteenthMonthlyWage: [
    "13th monthly wage (generally 1/12 of basic wage)",
    "13th monthly wage",
    "13th wage",
    "Osobní ohodnocení / Osobní příplatek",
    "Company Bonuses",
    "Firemní bonusy",
    "Firemné bonusy",
  ],
  monthlyBonus: [
    "Monthly Bonus",
    "Monthly bonus",
    "Měsíční bonus",
    "Mesačný bonus",
  ],
  standardBonus: [
    "Standard bonus",
    "standard bonus",
    "standard-bonus",
    "Standardní bonus",
    "Štandardný bonus",
  ],
  specialBonus: [
    "Special bonus",
    "special bonus",
    "special-bonus",
    "Speciální bonus",
    "Špeciálny bonus",
  ],
  fiftyFivePlusBonus: [
    "55 plus bonus",
    "55-plus bonus",
    "55 plus-bonus",
    "55+ bonus",
    "55-plus-bonus",
    "Bonus 55+",
  ],
  retirementBenefit: [
    "Retirement Benefit",
    "Retirement benefit",
    "Odchodné do důchodu",
    "Odchodné do dôchodku",
  ],
  severancePay: ["Severance Pay", "Severance pay", "Odstupné"],
  specialPayments: [
    "Special payments (1/12 of annual total)",
    "Special payments",
    "Zvláštní odměny",
    "Employee Benefits",
    "Zaměstnanecké výhody",
    "Zamestnanecké výhody",
  ],
  supplementaryPensionInsurance: [
    "Supplementary pension insurance contributions",
    "Příspěvek na doplňkové penzijní spoření",
    "Doplňkové penzijní pojištění",
    "Supplementary Pension Insurance",
    "Doplnkové dôchodkové poistenie",
  ],
  lifeInsurance: [
    "Life insurance contributions",
    "Životní pojištění",
    "Life Insurance",
    "Životné poistenie",
  ],
  mealVouchers: ["Meal Vouchers", "Stravenky", "Stravné", "Stravovacie lístky"],
  cafeteria: [
    "Cafeteria",
    "Cafeteria benefits",
    "Cafeterie benefity",
    "Cafeteria benefity",
    "Cafeteria Benefits",
    "Výhody jedálne",
  ],
  carAllowance: [
    "Car Allowance",
    "Příspěvek na auto",
    "Příspěvek na automobil",
    "Príspevok na auto",
  ],
  employeeContribution: [
    "Employee Contribution",
    "Employee contribution",
    "Příspěvek zaměstnance",
    "Príspevok zamestnanca",
  ],
  usualWeeklyWorkingHours: [
    "Usual weekly working hours in company (for full-time employees, i.e. activity rate = 100%)",
    "Usual weekly working hours in company",
    "Weekly working hours",
    "Average Weekly Hours",
    "Obvyklý týdenní fond pracovní doby",
    "Průměrné týdenní hodiny",
    "Priemerný týždenný počet hodín",
  ],
  usualAnnualWorkingHours: [
    "Usual annual working hours in the reference year (for full-time employees, i.e. activity rate = 100%)",
    "Usual annual working hours in the reference year",
    "Annual working hours",
    "Average Annual Hours",
    "Obvyklý roční fond pracovní doby",
    "Průměrné roční hodiny",
    "Priemerný ročný počet hodín",
  ],
  statisticalPopulation: [
    "Statistical population",
    "Population",
    "Typy smluvních vztahů",
    "Statistická populace",
    "Štatistická populácia",
  ],
  remarks: [
    "Remarks",
    "Poznámky",
  ],
  additionalInformation01: [
    "Additional information 1",
    "Additional information1",
    "Doplňující informace 1",
    "Doplňujúce informácie 1",
  ],
  additionalInformation02: [
    "Additional information 2",
    "Additional information2",
    "Doplňující informace 2",
    "Doplňujúce informácie 2",
  ],
  additionalInformation03: [
    "Additional information 3",
    "Additional information3",
    "Doplňující informace 3",
    "Doplňujúce informácie 3",
  ],
  additionalInformation04: [
    "Additional information 4",
    "Additional information4",
    "Doplňující informace 4",
    "Doplňujúce informácie 4",
  ],
  additionalInformation05: [
    "Additional information 5",
    "Additional information5",
    "Doplňující informace 5",
    "Doplňujúce informácie 5",
  ],
};

/**
 * Find the header row index in the data
 */
export const findHeaderRowIndex = (rows: string[][]): number => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    if (!Array.isArray(row)) continue;
    const rowStr = row.join(" ").toLowerCase();

    const hasKey =
      rowStr.includes("key") ||
      rowStr.includes("id") ||
      rowStr.includes("personnel");
    const hasAge = rowStr.includes("age") || rowStr.includes("birth");
    const hasGender =
      rowStr.includes("gender") || rowStr.includes("sex") || rowStr.includes("pohl");
    const hasSalary =
      rowStr.includes("wage") ||
      rowStr.includes("salary") ||
      rowStr.includes("basic");

    if (
      (hasKey && hasAge) ||
      (hasKey && hasGender) ||
      (hasAge && hasGender) ||
      hasSalary
    ) {
      return i;
    }
  }

  return -1;
};

/**
 * Extract raw gender values from rows for format auto-detection
 */
export const extractRawGenderValues = (rows: string[][]): string[] => {
  const headerRowIndex = findHeaderRowIndex(rows);

  if (headerRowIndex === -1) return [];

  const headerRow = rows[headerRowIndex].map((c) => String(c ?? ""));

  // Find gender column index
  const genderColumnIndex = headerRow.findIndex((header) => {
    const normalized = normalize(header).toLowerCase();

    return ["gender", "sex", "pohlaví"].some((term) => normalized.includes(term));
  });

  if (genderColumnIndex === -1) return [];

  // Extract raw gender values from data rows
  const dataRows = rows.slice(headerRowIndex + 1);

  return dataRows
    .map((row) => String(row[genderColumnIndex] ?? ""))
    .filter((v) => v.trim() !== "");
};

/**
 * Get a field value from row data using possible header names
 */
const getFieldValue = (
  rowData: Record<string, string>,
  possibleKeys: string[],
): string => {
  for (const pk of possibleKeys) {
    const normalizedPk = normalize(pk).toLowerCase();

    for (const [header, value] of Object.entries(rowData)) {
      if (normalize(header).toLowerCase() === normalizedPk) {
        return value || "";
      }
    }
  }

  return "";
};

/**
 * Map a single row to an EmployeeData object
 */
const mapRowToEmployee = (
  rowData: Record<string, string>,
  genderFormat: GenderFormat,
): EmployeeData => {
  const getField = (fieldName: keyof EmployeeData): string =>
    getFieldValue(rowData, FIELD_MAPPINGS[fieldName]);

  return {
    key: getField("key"),
    age: parseNumericField(getField("age")),
    gender: parseGenderField(getField("gender"), genderFormat),
    yearsOfService: parseNumericField(getField("yearsOfService")),
    educationAndTraining: getField("educationAndTraining"),
    employeeScarcity: parseNumericField(getField("employeeScarcity")),
    employeeEvaluation: parseNumericField(getField("employeeEvaluation")),
    category: getField("category"),
    function: getField("function"),
    levelOfRequirements: getField("levelOfRequirements"),
    levelOfRequirements2: getField("levelOfRequirements2"),
    levelOfRequirements3: getField("levelOfRequirements3"),
    professionalPosition: getField("professionalPosition"),
    activityRatePercent: parseNumericField(getField("activityRatePercent")),
    paidHours: parseNumericField(getField("paidHours")),
    basicWage: parseNumericField(getField("basicWage")),
    wageCompensationForVacation: parseNumericField(
      getField("wageCompensationForVacation"),
    ),
    wageCompensationDoctorFamilyLeave: parseNumericField(
      getField("wageCompensationDoctorFamilyLeave"),
    ),
    sickLeaveIncomeCompensation: parseNumericField(
      getField("sickLeaveIncomeCompensation"),
    ),
    allowances: parseNumericField(getField("allowances")),
    overtimeWage: parseNumericField(getField("overtimeWage")),
    overtimeAllowance: parseNumericField(getField("overtimeAllowance")),
    riskAllowance: parseNumericField(getField("riskAllowance")),
    standbyDuty: parseNumericField(getField("standbyDuty")),
    thirteenthMonthlyWage: parseNumericField(getField("thirteenthMonthlyWage")),
    monthlyBonus: parseNumericField(getField("monthlyBonus")),
    standardBonus: parseNumericField(getField("standardBonus")),
    specialBonus: parseNumericField(getField("specialBonus")),
    fiftyFivePlusBonus: parseNumericField(getField("fiftyFivePlusBonus")),
    retirementBenefit: parseNumericField(getField("retirementBenefit")),
    severancePay: parseNumericField(getField("severancePay")),
    specialPayments: parseNumericField(getField("specialPayments")),
    supplementaryPensionInsurance: parseNumericField(
      getField("supplementaryPensionInsurance"),
    ),
    lifeInsurance: parseNumericField(getField("lifeInsurance")),
    mealVouchers: parseNumericField(getField("mealVouchers")),
    cafeteria: parseNumericField(getField("cafeteria")),
    carAllowance: parseNumericField(getField("carAllowance")),
    employeeContribution: parseNumericField(getField("employeeContribution")),
    usualWeeklyWorkingHours: parseNumericField(
      getField("usualWeeklyWorkingHours") || "40",
    ),
    usualAnnualWorkingHours: parseNumericField(
      getField("usualAnnualWorkingHours") || "2088",
    ),
    statisticalPopulation: parseNumericField(
      getField("statisticalPopulation") || "1",
    ),
    remarks: getField("remarks"),
    additionalInformation01: getField("additionalInformation01"),
    additionalInformation02: getField("additionalInformation02"),
    additionalInformation03: getField("additionalInformation03"),
    additionalInformation04: getField("additionalInformation04"),
    additionalInformation05: getField("additionalInformation05"),
  };
};

/**
 * Parse raw rows into EmployeeData array
 */
export const parseRowsToEmployees = (rows: string[][]): EmployeeData[] => {
  try {
    const headerRowIndex = findHeaderRowIndex(rows);

    if (headerRowIndex === -1) {
      return [];
    }

    // Auto-detect gender format from raw values
    const rawGenderValues = extractRawGenderValues(rows);
    const detectedFormat = detectGenderFormat(rawGenderValues);

    const headerRow = rows[headerRowIndex].map((c) => String(c ?? ""));
    const dataRows = rows.slice(headerRowIndex + 1);

    const data = dataRows
      .map((row): EmployeeData => {
        const rowData: Record<string, string> = {};

        headerRow.forEach((header, colIndex) => {
          rowData[header] = String(row[colIndex] ?? "");
        });

        return mapRowToEmployee(rowData, detectedFormat);
      })
      .filter((emp) => (emp.key ?? "").toString().trim() !== "");

    return data;
  } catch {
    return [];
  }
};
